import React, { useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import A11ySvg from 'components/A11ySvg';
import RedCloseIcon from 'resources/red_close_icon.png';
import './RewardCalendar.css';

const RewardCalendar = ({
  show,
  onClose,
  validStartDate,
  validEndDate,
  blackoutDates,
  dailyValidFromTime,
  dailyValidToTime
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment().utc());
  const selectedDate = moment().utc();

  const isDateInRange = day => {
    const validStart = moment(validStartDate).utc().startOf('day');
    const validEnd = moment(validEndDate).utc().startOf('day');
    return day.isBetween(validStart, validEnd, null, '[]');
  };

  const isDateInBlackout = day => {
    return blackoutDates?.some(date => day.isSame(moment(date), 'day'));
  };

  const renderHeader = () => {
    return (
      <div className="reward-calendar-header">
        <h1 className="valid-dates-header">VALID DATES</h1>
        <h2 className="selected-month-header">
          {currentMonth.format('MMMM YYYY')}
        </h2>
      </div>
    );
  };

  const renderMonthSelect = () => {
    const prevMonths = [
      currentMonth.clone().subtract(2, 'months'),
      currentMonth.clone().subtract(1, 'month')
    ];
    const nextMonths = [
      currentMonth.clone().add(1, 'month'),
      currentMonth.clone().add(2, 'month')
    ];

    return (
      <div className="month-select">
        <div className="month-carousel">
          {prevMonths.map((month, i) => (
            <button
              className="month"
              key={month.format('MM-YYYY')}
              onClick={i === 1 ? prevMonth : secondPrevMonth}
              type="button"
            >
              {month.format('MMMM')}
            </button>
          ))}
          <span className="month selected">
            <span>{currentMonth.format('MMMM')}</span>
          </span>
          {nextMonths.map((month, i) => (
            <button
              className="month"
              key={month.format('MM-YYYY')}
              onClick={i === 0 ? nextMonth : secondNextMonth}
              type="button"
            >
              {month.format('MMMM')}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = currentMonth.clone().startOf('month');
    const monthEnd = currentMonth.clone().endOf('month');
    const rows = [];
    let days = [];
    let day = monthStart.clone();
    let formattedDate = '';

    while (day.isSameOrBefore(monthEnd, 'day')) {
      for (let i = 0; i < 7; i++) {
        if (day.isSameOrBefore(monthEnd, 'day')) {
          formattedDate = day.format('D');
          let cellClass = 'col cell';

          if (day.isSame(selectedDate, 'day')) {
            cellClass += ' selected';
          }
          if (isDateInBlackout(day)) {
            cellClass += ' blackout';
          }
          if (isDateInRange(day)) {
            cellClass += ' valid';
          }

          days.push(
            <div className={cellClass} key={day.toString()}>
              <span className="number-wrapper">
                {cellClass.includes('selected') ? (
                  <A11ySvg viewBox="0 0 50 50" alt="Current day of the month">
                    <defs>
                      <linearGradient
                        id="gradient-stroke"
                        gradientTransform="rotate(90)"
                      >
                        <stop offset="0%" stopColor="#377cf5" />
                        <stop offset="100%" stopColor="#29fcc6" />
                      </linearGradient>
                      <linearGradient
                        id="gradient-fill"
                        gradientTransform="rotate(90)"
                      >
                        <stop offset="0%" stopColor="#29fcc6" />
                        <stop offset="100%" stopColor="#377cf5" />
                      </linearGradient>
                    </defs>
                    <text
                      x="50%"
                      y="50%"
                      dominantBaseline="middle"
                      textAnchor="middle"
                      fill="none"
                      stroke="url(#gradient-stroke)"
                      strokeWidth="2"
                      paintOrder="stroke"
                      className="number"
                    >
                      {formattedDate}
                    </text>
                    <text
                      x="50%"
                      y="50%"
                      dominantBaseline="middle"
                      textAnchor="middle"
                      fill="url(#gradient-fill)"
                      stroke="none"
                      paintOrder="stroke"
                      className="number"
                    >
                      {formattedDate}
                    </text>
                  </A11ySvg>
                ) : (
                  <span className="number">{formattedDate}</span>
                )}
              </span>
            </div>
          );
          day.add(1, 'day');
        } else {
          days.push(
            <div
              className="col cell empty"
              key={`empty-${day.toString()}-${i}`}
            >
              <span className="number-wrapper">
                <span className="number"></span>
              </span>
            </div>
          );
        }
      }
      rows.push(
        <div className="row" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }

    return <div className="body">{rows}</div>;
  };

  const renderDailyValidTime = () => {
    if (isDateInBlackout(selectedDate)) {
      return <span className="blackout-date">UNAVAILABLE</span>;
    }

    if (isDateInRange(selectedDate)) {
      return (
        <span className="valid-date">
          {dailyValidFromTime &&
            `${moment(dailyValidFromTime, 'HH:mm:ss').format(
              'h:mma'
            )} - ${moment(dailyValidToTime, 'HH:mm:ss').format('h:mma')}`}
        </span>
      );
    }

    return '';
  };

  const renderColorLegend = () => {
    return (
      <div className="color-legend">
        <div className="color-square not-valid" />
        <span className="color-label">Not Valid</span>
        <div className="color-square valid" />
        <span className="color-label">Valid</span>
        <div className="color-square blackout" />
        <span className="color-label">Blackout</span>
        <div className="color-square current" />
        <span className="color-label">Current</span>
      </div>
    );
  };

  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  const prevMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  const secondNextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(2, 'month'));
  };

  const secondPrevMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(2, 'month'));
  };

  const onCloseCalendar = () => {
    setCurrentMonth(moment());
    onClose();
  };

  return (
    <Modal
      className={'reward-calendar-modal'}
      show={show}
      onHide={onCloseCalendar}
    >
      <Modal.Body>
        <div className="calendar">
          {renderHeader()}
          {renderMonthSelect()}
          {renderCells()}
        </div>
        <div className="daily-valid-time">{renderDailyValidTime()}</div>
        {renderColorLegend()}
        <button
          onClick={onCloseCalendar}
          className="close-modal-button"
          type="button"
        >
          <img src={RedCloseIcon} alt="Close Calendar" />
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default RewardCalendar;
